import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Combobox,
  RadioGroup,
  SearchInput,
  SelectDateRange,
  SelectMenu,
} from '@decisiv/ui-components';
import iconix from '@decisiv/iconix';
import { t } from '@lingui/macro';
import UserAvatar from './UserAvatar';
import { getAvatarColor, CATEGORY_COLORS, tint } from './utils';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100].map((n) => ({
  id: String(n),
  label: `${n} / page`,
  value: String(n),
}));

const Bar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px 14px;
  margin-bottom: 16px;
  background: var(--if-surface-1);
  border: 1px solid var(--if-border);
  border-radius: 6px;
`;

const SearchWrapper = styled.div`
  flex-shrink: 0;
  width: 240px;
`;

const ComboboxWrapper = styled.div`
  flex-shrink: 0;
  width: 300px;
`;

const ChipsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  flex: 1;
`;

const UserChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px 3px 4px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid
    ${({ active, chipColor }) => (active ? chipColor : 'var(--if-border)')};
  background: ${({ active, chipColor }) =>
    active ? tint(chipColor, 18) : 'var(--if-surface-1)'};
  color: ${({ active, chipColor }) => (active ? chipColor : 'var(--if-ink-1)')};
  transition: all 0.15s ease;

  &:hover {
    border-color: ${({ chipColor }) => chipColor};
  }
`;

const CategoryChip = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid
    ${({ active, chipColor }) => (active ? chipColor : 'var(--if-border)')};
  background: ${({ active, chipColor }) =>
    active ? tint(chipColor, 18) : 'var(--if-surface-1)'};
  color: ${({ active, chipColor }) => (active ? chipColor : 'var(--if-ink-1)')};
  transition: all 0.15s ease;

  &:hover {
    border-color: ${({ chipColor }) => chipColor};
  }
`;

const DateRangeWrapper = styled.div`
  flex-shrink: 0;
  width: 300px;
`;

const PageSizeWrapper = styled.div`
  flex-shrink: 0;
  width: 130px;
`;

const ResultCount = styled.span`
  font-size: 12px;
  color: var(--if-ink-3);
  white-space: nowrap;
`;

export default function FilterBar({
  searchTerm,
  onSearchChange,
  authors,
  activeAuthors,
  onToggleAuthor,
  categories = [],
  activeCategories = [],
  onToggleCategory = undefined,
  dateRange = undefined,
  onDateRangeChange,
  resultCount,
  totalCount = undefined,
  pageSize = 10,
  onPageSizeChange = undefined,
  locked = false,
  mode = undefined,
  onModeChange = undefined,
  comboInput = '',
  comboOptions = [],
  comboLoading = false,
  onComboInputChange = undefined,
  onComboValueChange = undefined,
}) {
  return (
    <Bar>
      {!locked && mode !== undefined && onModeChange && (
        <RadioGroup
          name="radio-history-mode"
          items={[
            { label: t`Integration`, value: 'global' },
            { label: t`Locations`, value: 'location' },
          ]}
          value={mode}
          onChange={(e) => onModeChange(e.target.value)}
        />
      )}
      {!locked && mode === 'location' && (
        <ComboboxWrapper>
          <Combobox
            label={t`Search location`}
            name="history-location-search"
            hideLabel
            icon={iconix.Search}
            inputValue={comboInput}
            options={comboOptions}
            loading={comboLoading}
            variant="async"
            onChange={onComboInputChange}
            onChangeInputValue={onComboInputChange}
            onChangeValue={onComboValueChange}
          />
        </ComboboxWrapper>
      )}
      <SearchWrapper>
        <SearchInput
          hideLabel
          name="history-search"
          label={t`Search`}
          placeholder={t`Search fields, values, users...`}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          size="small"
        />
      </SearchWrapper>
      <DateRangeWrapper>
        <SelectDateRange
          hideLabel
          label={t`Date Range`}
          name="history-date-range"
          icon={iconix.Calendar}
          onChange={onDateRangeChange}
          values={dateRange}
          zIndex={999}
          // Under createRoot, KDS's document-level click-outside listener
          // sees the popover already open and closes it on the same click.
          onClick={(e) => e.stopPropagation()}
        />
      </DateRangeWrapper>
      <ChipsWrapper>
        {categories.map((category) => {
          const active = activeCategories.includes(category);
          return (
            <CategoryChip
              key={category}
              type="button"
              active={active}
              chipColor={CATEGORY_COLORS[category] || 'var(--if-accent)'}
              onClick={() => onToggleCategory(category)}
            >
              {category}
            </CategoryChip>
          );
        })}
        {authors.map((author) => {
          const active = activeAuthors.includes(author);
          return (
            <UserChip
              key={author}
              type="button"
              active={active}
              chipColor={getAvatarColor(author)}
              onClick={() => onToggleAuthor(author)}
            >
              <UserAvatar name={author} size={18} />
              {author}
            </UserChip>
          );
        })}
      </ChipsWrapper>
      {onPageSizeChange && (
        <PageSizeWrapper>
          <SelectMenu
            label={`${pageSize} / page`}
            name="history-page-size"
            options={PAGE_SIZE_OPTIONS}
            value={[String(pageSize)]}
            selected={null}
            onChange={(val) =>
              onPageSizeChange(Number(Array.isArray(val) ? val[0] : val))
            }
            zIndex={999}
          />
        </PageSizeWrapper>
      )}
      <ResultCount>
        {totalCount !== undefined && resultCount < totalCount
          ? `${resultCount} of ${totalCount} changes`
          : `${resultCount} changes`}
      </ResultCount>
    </Bar>
  );
}

FilterBar.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  authors: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeAuthors: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleAuthor: PropTypes.func.isRequired,
  categories: PropTypes.arrayOf(PropTypes.string),
  activeCategories: PropTypes.arrayOf(PropTypes.string),
  onToggleCategory: PropTypes.func,
  dateRange: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  onDateRangeChange: PropTypes.func.isRequired,
  resultCount: PropTypes.number.isRequired,
  pageSize: PropTypes.number,
  onPageSizeChange: PropTypes.func,
  totalCount: PropTypes.number,
  locked: PropTypes.bool,
  mode: PropTypes.string,
  onModeChange: PropTypes.func,
  comboInput: PropTypes.string,
  comboOptions: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),
  comboLoading: PropTypes.bool,
  onComboInputChange: PropTypes.func,
  onComboValueChange: PropTypes.func,
};
